import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Typography,
  Table,
  Tag,
  Tooltip,
  Grid,
} from "antd";
import { SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axiosNew from "../../../store/api/axiosNew";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const RetailTransactionsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const userRole = localStorage.getItem("role");
  const token = localStorage.getItem("accessToken");

  // ---------------- FETCH ----------------
  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);

      const url = "/admin/retail/transactions";

      const res = await axiosNew.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: pagination.limit },
      });

      const data = res.data?.data || [];

      setTransactions(data);
      setFilteredTransactions(data);

      setPagination((prev) => ({
        ...prev,
        page: res.data?.page || page,
        total: res.data?.totalCount || 0,
      }));
    } catch (error) {
      console.error("❌ Failed to fetch retail transactions", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(1);
  }, []);

  // ---------------- SEARCH ----------------
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = transactions.filter(
      (txn) =>
        txn.transactionId?.toLowerCase().includes(term) ||
        txn.retailName?.toLowerCase().includes(term) ||
        txn.title?.toLowerCase().includes(term),
    );
    setFilteredTransactions(filtered);
  }, [searchTerm, transactions]);

  const handleInvoiceOpen = (id) => {
    const url = `/admin/transactions/${id}/invoice`;
    window.open(url, "_blank");
  };

  // ---------------- COLUMNS ----------------
  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      width: 120,
      render: (date) => (
        <div>
          <div>{moment(date).format("DD-MM-YYYY")}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {moment(date).format("HH:mm")}
          </Text>
        </div>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      render: (id) => (
        <Text copyable style={{ fontStyle: "italic" }}>
          {id}
        </Text>
      ),
    },
    ...(userRole === "admin"
      ? [
          {
            title: "User",
            dataIndex: "retailName",
            render: (name, record) => (
              <Tooltip title={record.retailEmail}>
                <Text strong>{name}</Text>
              </Tooltip>
            ),
          },
        ]
      : []),
    {
      title: "Leads",
      dataIndex: "purchasedItems",
      render: (items) => (
        <>
          {items?.map((item, idx) => (
            <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
              {item.name} <Text style={{ fontSize: 10 }}>({item.purpose})</Text>
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (amt) => <Text strong>₹{amt?.toFixed(2)}</Text>,
    },

    // {
    //   title: "Invoice",
    //   dataIndex: "transactionId",
    //   align: "center",
    //   render: (transactionId) => (
    //     <Tag
    //       color="geekblue"
    //       style={{ cursor: "pointer" }}
    //       onClick={() => handleInvoiceOpen(transactionId)}
    //     >
    //       View
    //     </Tag>
    //   ),
    // },
    {
      title: "Invoice",
      align: "center",
      render: (_, record) => (
        <Tag
          color="geekblue"
          style={{ cursor: "pointer" }}
          onClick={() => handleInvoiceOpen(record._id)}
        >
          View
        </Tag>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag
          color={status === "completed" ? "green" : "orange"}
          icon={status === "completed" ? null : <InfoCircleOutlined />}
        >
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  // ---------------- UI ----------------
  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
      <Title level={3}>
        {userRole === "admin"
          ? "Retail Transactions (Admin)"
          : "My Purchase History"}
      </Title>

      <Card
        style={{
          marginTop: 20,
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        extra={
          <Input
            placeholder="Search TXN ID or Lead Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: isMobile ? 200 : 300 }}
          />
        }
      >
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={filteredTransactions}
          loading={loading}
          size={isMobile ? "small" : "middle"}
          scroll={isMobile ? { x: 900 } : undefined}
          onChange={(p) => fetchTransactions(p.current)}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
          }}
        />
      </Card>
    </div>
  );
};

export default RetailTransactionsHistory;
